import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { API_URL, pathLogin } from "../../config/constants";
import VippstarLogo from "../../assets/vippstar_logo.png";
import {
  Container, TopBar, Content, PageTitle, Card, SectionTitle, Grid,
  Field, Label, Input, Textarea, Hint, SubmitButton, LinkText,
} from "./styles";

const INITIAL = {
  display_name: "",
  bio: "",
  hospital_code: "",
  username: "",
  password: "",
  confirmPassword: "",
  email: "",
};

// Mismas reglas que anuncian las ayudas bajo cada campo.
const NAME_MAX = 150;
const BIO_MAX = 500;

const isUsernameValid = (v) => /^[a-zA-Z0-9]{8,}$/.test(v);
const isPasswordValid = (v) =>
  v.length >= 8 && /[a-zA-Z]/.test(v) && /[0-9]/.test(v) && /[^a-zA-Z0-9]/.test(v);

const Register = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [form, setForm] = useState(INITIAL);
  const [touched, setTouched] = useState({});
  const [errorField, setErrorField] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errorField === field) setErrorField("");
  };

  const markTouched = (field) => () => setTouched((prev) => ({ ...prev, [field]: true }));

  const invalid = {
    display_name: !form.display_name.trim(),
    hospital_code: !form.hospital_code.trim(),
    username: !isUsernameValid(form.username),
    password: !isPasswordValid(form.password),
    confirmPassword: !form.confirmPassword || form.confirmPassword !== form.password,
    email: !form.email.trim(),
  };

  // En rojo solo tras salir del campo, o si el servidor lo ha rechazado: así no
  // se ve todo en rojo nada más abrir el formulario.
  const showError = (field) => (touched[field] && invalid[field]) || errorField === field;

  const complete = !Object.values(invalid).some(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!complete || loading) return;

    setLoading(true);
    setErrorField("");
    try {
      const res = await fetch(`${API_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          display_name: form.display_name.trim(),
          bio: form.bio.trim(),
          hospital_code: form.hospital_code.trim(),
          username: form.username,
          password: form.password,
          email: form.email.trim(),
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setErrorField(data.field || "");
        const key = {
          hospital_code: "auth.errors.hospitalCode",
          username: "auth.errors.username",
          email: "auth.errors.email",
          password: "auth.errors.password",
        }[data.field] || "auth.registerError";
        toast.error(t(key));
        return;
      }

      toast.success(t("auth.accountCreated"));
      history.push(pathLogin);
    } catch {
      toast.error(t("auth.registerError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <TopBar>
        <img src={VippstarLogo} alt="Vippstar" style={{ width: 92 }} />
      </TopBar>

      <Content>
        <PageTitle>{t("auth.therapistRegistration")}</PageTitle>

        <Card onSubmit={handleSubmit}>
          <SectionTitle>{t("auth.publicInformation")}</SectionTitle>
          <Grid>
            <Field>
              <Label htmlFor="display_name">{t("auth.displayName")}</Label>
              <Input
                id="display_name"
                $error={showError("display_name")}
                maxLength={NAME_MAX}
                placeholder={t("auth.displayNamePlaceholder")}
                value={form.display_name}
                onChange={set("display_name")}
                onBlur={markTouched("display_name")}
              />
              <Hint $error={showError("display_name")}>{t("auth.displayNameHint")}</Hint>
            </Field>

            <Field>
              <Label htmlFor="bio">{t("auth.bio")}</Label>
              <Textarea
                id="bio"
                maxLength={BIO_MAX}
                placeholder={t("auth.bioPlaceholder")}
                value={form.bio}
                onChange={set("bio")}
              />
              <Hint>
                {t("auth.bioHint")} {form.bio.length}/{BIO_MAX}
              </Hint>
            </Field>
          </Grid>

          <SectionTitle>{t("auth.therapistAccount")}</SectionTitle>
          <Grid>
            <Field>
              <Label htmlFor="hospital_code">{t("auth.hospitalCode")}</Label>
              <Input
                id="hospital_code"
                $error={showError("hospital_code")}
                placeholder={t("auth.hospitalCodePlaceholder")}
                value={form.hospital_code}
                onChange={set("hospital_code")}
                onBlur={markTouched("hospital_code")}
              />
              <Hint $error={showError("hospital_code")}>{t("auth.hospitalCodeHint")}</Hint>
            </Field>

            <Field>
              <Label htmlFor="username">{t("auth.username")}</Label>
              <Input
                id="username"
                $error={showError("username")}
                placeholder={t("auth.usernamePlaceholderNew")}
                value={form.username}
                onChange={set("username")}
                onBlur={markTouched("username")}
              />
              <Hint $error={showError("username")}>{t("auth.usernameHint")}</Hint>
            </Field>

            <Field>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input
                id="password"
                type="password"
                $error={showError("password")}
                placeholder={t("auth.passwordPlaceholder")}
                value={form.password}
                onChange={set("password")}
                onBlur={markTouched("password")}
              />
              <Hint $error={showError("password")}>{t("auth.passwordHint")}</Hint>
            </Field>

            <Field>
              <Label htmlFor="confirmPassword">{t("auth.confirmPassword")}</Label>
              <Input
                id="confirmPassword"
                type="password"
                $error={showError("confirmPassword")}
                placeholder={t("auth.confirmPasswordPlaceholder")}
                value={form.confirmPassword}
                onChange={set("confirmPassword")}
                onBlur={markTouched("confirmPassword")}
              />
              <Hint $error={showError("confirmPassword")}>{t("auth.confirmPasswordHint")}</Hint>
            </Field>

            <Field>
              <Label htmlFor="email">{t("auth.emailAddress")}</Label>
              <Input
                id="email"
                type="email"
                $error={showError("email")}
                placeholder={t("auth.emailPlaceholderNew")}
                value={form.email}
                onChange={set("email")}
                onBlur={markTouched("email")}
              />
            </Field>
          </Grid>

          <SubmitButton type="submit" disabled={!complete || loading}>
            {loading ? t("auth.creatingAccount") : t("auth.createTherapistAccount")}
          </SubmitButton>
        </Card>

        <LinkText>
          {t("auth.haveAccount")} <Link to={pathLogin}>{t("auth.signIn")}</Link>
        </LinkText>
      </Content>
    </Container>
  );
};

export default Register;
